import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { memberSecurityStatusApi } from '@/api/member'
import router from '@/router'
import { useSessionStore } from '@/stores/session'
import {
  resolveSecurityPrompts,
  shouldRedirectToKycVerify,
  type MemberSecurityStatus,
  type SecurityPromptType,
} from '@/types/securityPrompt'
import { useAssetsStore } from './assets'

export const useSecurityPromptStore = defineStore('securityPrompt', () => {
  const status = ref<MemberSecurityStatus | null>(null)
  const queue = ref<SecurityPromptType[]>([])
  const checking = ref(false)
  /** 当前会话内用户主动关闭的弹窗，登出或重新登录后清空 */
  const dismissedInSession = ref<Set<SecurityPromptType>>(new Set())

  const currentPrompt = computed(() => queue.value[0] ?? null)
  const visible = computed(() => queue.value.length > 0)

  function applyQueue(next: SecurityPromptType[]) {
    queue.value = next.filter((item) => !dismissedInSession.value.has(item))
  }

  function maybeRedirectToKycVerify(data: MemberSecurityStatus) {
    if (!shouldRedirectToKycVerify(data)) return
    if (useAssetsStore().isAgent) return
    const current = router.currentRoute.value
    if (current.name === 'VerifyReal') return

    router.push({ name: 'VerifyReal' })
  }

  async function checkAndEnqueue() {
    const sessionStore = useSessionStore()
    if (!sessionStore.sessionId) return

    checking.value = true
    try {
      const { data } = await memberSecurityStatusApi()
      status.value = data
      applyQueue(resolveSecurityPrompts(data))

      if (queue.value.length === 0) {
        maybeRedirectToKycVerify(data)
      }
    } catch (error) {
      console.error('[SecurityPrompt] 获取安全状态失败:', error)
    } finally {
      checking.value = false
    }
  }

  function dismissCurrent() {
    const current = queue.value[0]
    if (current) {
      dismissedInSession.value = new Set([...dismissedInSession.value, current])
    }
    // 清空队列，避免后续弹出
    queue.value = []
  }

  async function onPromptCompleted(_type: SecurityPromptType) {
    queue.value = queue.value.slice(1)
    await checkAndEnqueue()
  }

  function resetSessionDismissals() {
    dismissedInSession.value = new Set()
  }

  /** 底部导航切换时根据缓存状态重新弹出未完成的安全引导 */
  function recheckOnNavClick() {
    const sessionStore = useSessionStore()
    if (!sessionStore.sessionId || !status.value) return

    resetSessionDismissals()
    applyQueue(resolveSecurityPrompts(status.value))

    if (queue.value.length === 0) {
      maybeRedirectToKycVerify(status.value)
    }
  }

  function clear() {
    status.value = null
    queue.value = []
    dismissedInSession.value = new Set()
  }

  return {
    status,
    queue,
    checking,
    currentPrompt,
    visible,
    checkAndEnqueue,
    dismissCurrent,
    onPromptCompleted,
    resetSessionDismissals,
    recheckOnNavClick,
    clear,
  }
})
